import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { X, Play, Pause, Plus, Trash2, Scissors, Merge, Check, AlertTriangle, ZoomIn, ZoomOut, Sparkles, MousePointer2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fixed colors for the built-in categories; palette fallback for future ones
const CAT_COLORS = {
  intimate: '#22c55e', violence: '#ef4444', nudity: '#3b82f6',
  audio_vulgar: '#eab308', visual_vulgar: '#a855f7',
};
const PALETTE = ['#14b8a6', '#f97316', '#ec4899', '#84cc16', '#06b6d4'];
const LOW_CONFIDENCE = 70;
const FRAME = 1 / 25; // frame-by-frame stepping (25 fps)

const fmt = (sec) => {
  sec = Math.max(0, Math.round(sec));
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};
const fmtF = (sec) => {
  sec = Math.max(0, sec);
  const whole = Math.floor(sec);
  const fr = Math.floor((sec - whole) * 25);
  const h = Math.floor(whole / 3600), m = Math.floor((whole % 3600) / 60), s = whole % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}:${String(fr).padStart(2, '0')}`;
};
const isPlayableVideoUrl = (url) => {
  const ext = (url.split('?')[0].split('.').pop() || '').toLowerCase();
  return !['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg', 'bmp'].includes(ext);
};
const safePlay = (v) => { if (v) v.play().catch(() => {}); };

const parseTs = (ts) => {
  if (typeof ts === 'number') return ts;
  const p = String(ts || '0').split(':').map(Number);
  if (p.length === 3) return p[0] * 3600 + p[1] * 60 + p[2];
  if (p.length === 2) return p[0] * 60 + p[1];
  return parseFloat(ts) || 0;
};

export default function FilterTimelineEditor({ contentId, episodeId = '', onClose, onValidated }) {
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]); // {key, filter_id, start, end, confidence, source}
  const [selected, setSelected] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [pxPerSec, setPxPerSec] = useState(4);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [flash, setFlash] = useState(null);
  const [tool, setTool] = useState('arrow'); // 'arrow' | 'zoom' (FCPX A/Z tools)
  const [altDown, setAltDown] = useState(false);
  const [videoError, setVideoError] = useState(null);
  const videoRef = useRef(null);
  const scrollRef = useRef(null);
  const rulerRef = useRef(null);
  const drag = useRef(null);
  const itemsRef = useRef([]);
  const history = useRef([]);
  const redoStack = useRef([]);
  const flashTimer = useRef(null);
  useEffect(() => { itemsRef.current = items; }, [items]);

  // ── FCPX-style undo/redo ──
  const pushHistory = useCallback(() => {
    history.current.push(itemsRef.current);
    if (history.current.length > 50) history.current.shift();
    redoStack.current = [];
  }, []);
  const undo = useCallback(() => {
    const prev = history.current.pop();
    if (prev) { redoStack.current.push(itemsRef.current); setItems(prev); }
  }, []);
  const redo = useCallback(() => {
    const nxt = redoStack.current.pop();
    if (nxt) { history.current.push(itemsRef.current); setItems(nxt); }
  }, []);

  const colorOf = useCallback((cid) => CAT_COLORS[cid] || PALETTE[cats.findIndex(c => c.id === cid) % PALETTE.length], [cats]);

  // ── load detections + validated filters ──
  useEffect(() => {
    axios.get(`${API}/admin/ai-filters/detections/${contentId}`, { params: { episode_id: episodeId }, withCredentials: true })
      .then(({ data }) => {
        setCats(data.categories || []);
        setVideoUrl(data.video_url || '');
        const list = [];
        if (data.reviewed || !(data.detections || []).length) {
          // Already validated (or no AI run): edit the current filters
          (data.current_filters || []).forEach((f, i) => list.push({
            key: `cf${i}`, filter_id: f.filter_id || f.type, start: parseTs(f.start), end: parseTs(f.end),
            confidence: f.confidence ?? null, source: f.source || 'manual',
          }));
        } else {
          // Pending review: AI detections + manual filters kept
          (data.current_filters || []).filter(f => f.source !== 'ai').forEach((f, i) => list.push({
            key: `cf${i}`, filter_id: f.filter_id || f.type, start: parseTs(f.start), end: parseTs(f.end),
            confidence: null, source: 'manual',
          }));
          (data.detections || []).forEach(d => list.push({
            key: d.id, filter_id: d.filter_id, start: d.start, end: d.end,
            confidence: d.confidence ?? null, source: 'ai',
          }));
        }
        setItems(list.filter(i => i.filter_id));
        setLoaded(true);
      }).catch(() => setLoaded(true));
  }, [contentId, episodeId]);

  const upd = (key, patch) => setItems(prev => prev.map(i => i.key === key ? { ...i, ...patch } : i));
  const sel = items.find(i => i.key === selected);
  const lowConf = useMemo(() => items.filter(i => i.confidence !== null && i.confidence < LOW_CONFIDENCE), [items]);
  const catAction = useMemo(() => Object.fromEntries(cats.map(c => [c.id, c.action])), [cats]);
  const catName = useMemo(() => Object.fromEntries(cats.map(c => [c.id, c.name])), [cats]);

  const showFlash = (msg) => {
    setFlash(msg);
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(null), 1600);
  };

  // ── filter preview during playback (test mode) ──
  const onTimeUpdate = (e) => {
    const v = e.target;
    const t = v.currentTime;
    setCurrentTime(t);
    if (!testMode) return;
    let mute = false;
    for (const i of itemsRef.current) {
      if (t >= i.start && t < i.end) {
        if (catAction[i.filter_id] === 'mute') mute = true;
        else {
          v.currentTime = i.end + 0.05;
          showFlash(`⏭ Scène filtrée : ${catName[i.filter_id] || i.filter_id}`);
          return;
        }
      }
    }
    if (v.muted !== mute) {
      v.muted = mute;
      if (mute) showFlash(`🔇 Son coupé : vulgarité`);
    }
  };

  // ── zoom centered on an anchor time (playhead by default, FCPX-like) ──
  const zoomAt = (factor, anchorSec) => {
    const anchor = anchorSec ?? currentTime;
    setPxPerSec(p => {
      const np = Math.max(1, Math.min(48, p * factor));
      setTimeout(() => {
        const el = scrollRef.current;
        if (el) el.scrollLeft = Math.max(0, 130 + anchor * np - el.clientWidth / 2);
      }, 0);
      return np;
    });
  };

  // ── FCPX-style keyboard shortcuts ──
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'select' || tag === 'textarea') return;
      const mod = e.metaKey || e.ctrlKey;
      if (e.code === 'Space') {
        e.preventDefault();
        const v = videoRef.current;
        if (v) { if (v.paused) safePlay(v); else v.pause(); }
      } else if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo(); else undo();
      } else if (mod && e.key.toLowerCase() === 'b') {
        e.preventDefault();
        splitSelRef.current?.();
      } else if ((e.key === 'Backspace' || e.key === 'Delete')) {
        e.preventDefault();
        removeSelRef.current?.();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        const v = videoRef.current;
        if (v) {
          v.pause();
          const step = e.shiftKey ? 1 : FRAME;
          v.currentTime = Math.max(0, v.currentTime + (e.key === 'ArrowLeft' ? -step : step));
        }
      } else if (e.key === '=' || e.key === '+') { zoomAt(2); }
      else if (e.key === '-') { zoomAt(0.5); }
      else if (!mod && e.key.toLowerCase() === 'z') { e.preventDefault(); setTool('zoom'); }
      else if (!mod && e.key.toLowerCase() === 'a') { e.preventDefault(); setTool('arrow'); }
      else if (e.key === 'Alt') { setAltDown(true); }
    };
    const onKeyUp = (e) => { if (e.key === 'Alt') setAltDown(false); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKeyUp);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('keyup', onKeyUp); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [undo, redo, testMode, currentTime]);
  const splitSelRef = useRef(null);
  const removeSelRef = useRef(null);

  // ── block drag / resize (pointer events) ──
  const onBlockDown = (e, item, mode) => {
    e.stopPropagation(); e.preventDefault();
    if (tool === 'zoom') { zoomAt(e.altKey ? 0.5 : 2, item.start); return; }
    setSelected(item.key);
    pushHistory();
    drag.current = { key: item.key, mode, x0: e.clientX, y0: e.clientY, start0: item.start, end0: item.end, catIdx0: Math.max(0, cats.findIndex(c => c.id === item.filter_id)) };
    const move = (ev) => {
      const d = drag.current; if (!d) return;
      const ds = (ev.clientX - d.x0) / pxPerSec;
      if (d.mode === 'move') {
        const len = d.end0 - d.start0;
        const ns = Math.max(0, Math.min((duration || 1e9) - len, d.start0 + ds));
        // FCPX-like: dragging vertically moves the block to another track = changes its type
        const rowDelta = Math.round((ev.clientY - d.y0) / 37);
        const newIdx = Math.max(0, Math.min(cats.length - 1, d.catIdx0 + rowDelta));
        upd(d.key, { start: ns, end: ns + len, filter_id: cats[newIdx]?.id || itemsRef.current.find(i => i.key === d.key)?.filter_id });
      } else if (d.mode === 'l') {
        upd(d.key, { start: Math.max(0, Math.min(d.end0 - 1, d.start0 + ds)) });
      } else {
        upd(d.key, { end: Math.min(duration || 1e9, Math.max(d.start0 + 1, d.end0 + ds)) });
      }
    };
    const up = () => { drag.current = null; window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const seekTo = (sec) => { const v = videoRef.current; if (v) { v.currentTime = Math.max(0, Math.min(duration || 1e9, sec)); } };
  const setRate = (s) => { setSpeed(s); const v = videoRef.current; if (v) v.playbackRate = s; };
  // ── FCPX-like scrub: pointer down + drag moves the playhead; zoom tool clicks zoom in/out ──
  const onScrubDown = (e) => {
    const el = e.currentTarget;
    const timeAt = (ev) => (ev.clientX - el.getBoundingClientRect().left) / pxPerSec;
    if (tool === 'zoom') { zoomAt(e.altKey ? 0.5 : 2, timeAt(e)); return; }
    e.preventDefault();
    seekTo(timeAt(e));
    const move = (ev) => seekTo(timeAt(ev));
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  const onPlayheadDown = (e) => {
    e.stopPropagation(); e.preventDefault();
    const el = rulerRef.current; if (!el) return;
    const move = (ev) => seekTo((ev.clientX - el.getBoundingClientRect().left) / pxPerSec);
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  // ── actions ──
  const addFilter = () => {
    pushHistory();
    const key = `m${Date.now()}`;
    setItems(prev => [...prev, { key, filter_id: cats[0]?.id || 'intimate', start: currentTime, end: Math.min(duration || currentTime + 10, currentTime + 10), confidence: null, source: 'manual' }]);
    setSelected(key);
  };
  const removeSel = () => { if (!selected) return; pushHistory(); setItems(prev => prev.filter(i => i.key !== selected)); setSelected(null); };
  const splitSel = () => {
    if (!sel) return;
    pushHistory();
    const at = currentTime > sel.start + 0.5 && currentTime < sel.end - 0.5 ? currentTime : (sel.start + sel.end) / 2;
    const k2 = `s${Date.now()}`;
    setItems(prev => prev.flatMap(i => i.key !== sel.key ? [i] : [
      { ...i, end: at }, { ...i, key: k2, start: at },
    ]));
  };
  const mergeSel = () => {
    if (!sel) return;
    const next = items.filter(i => i.key !== sel.key && i.filter_id === sel.filter_id && i.start >= sel.start)
      .sort((a, b) => a.start - b.start)[0];
    if (!next) return;
    pushHistory();
    setItems(prev => prev.filter(i => i.key !== next.key).map(i => i.key === sel.key
      ? { ...i, end: Math.max(i.end, next.end), confidence: i.confidence !== null && next.confidence !== null ? Math.round((i.confidence + next.confidence) / 2) : i.confidence }
      : i));
  };
  splitSelRef.current = splitSel;
  removeSelRef.current = removeSel;
  const validate = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/ai-filters/validate`, {
        content_id: contentId, episode_id: episodeId,
        filters: items.map(i => ({ filter_id: i.filter_id, start: i.start, end: i.end, confidence: i.confidence, source: i.source })),
      }, { withCredentials: true });
      onValidated?.(data.content_filters);
      onClose();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur de validation'); }
    finally { setSaving(false); }
  };

  const timelineW = Math.max(600, (duration || 60) * pxPerSec);
  const marks = useMemo(() => {
    const step = pxPerSec >= 8 ? 10 : pxPerSec >= 3 ? 30 : 60;
    const arr = [];
    for (let t = 0; t <= (duration || 0); t += step) arr.push(t);
    return arr;
  }, [duration, pxPerSec]);

  return (
    <div className="fixed inset-0 z-[100] bg-[#0b0b12] flex flex-col" data-testid="filter-timeline-editor">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
        <div className="flex items-center gap-2 text-white">
          <Sparkles size={16} className="text-purple-400" />
          <h2 className="text-sm font-bold">Vérification des filtres — validation admin</h2>
          {lowConf.length > 0 && (
            <span className="flex items-center gap-1 text-[10px] text-orange-400 bg-orange-500/10 px-2 py-0.5 rounded-full" data-testid="low-confidence-count">
              <AlertTriangle size={10} /> {lowConf.length} détection(s) à vérifier (score &lt; {LOW_CONFIDENCE}%)
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={validate} disabled={saving || !loaded}
            className="flex items-center gap-1.5 px-4 py-2 bg-green-600 hover:bg-green-500 text-white text-xs font-bold rounded-lg transition-colors disabled:opacity-50"
            data-testid="validate-filters-btn">
            <Check size={13} /> {saving ? 'Validation…' : 'Valider définitivement les filtres'}
          </button>
          <button onClick={onClose} className="p-2 text-white/60 hover:text-white" data-testid="timeline-close-btn"><X size={18} /></button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Left: video + timeline */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="bg-black flex items-center justify-center relative" style={{ height: '38%' }}>
            {flash && (
              <div className="absolute top-3 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-black/80 border border-white/20 rounded-full text-white text-xs font-semibold z-10 pointer-events-none" data-testid="filter-test-flash">
                {flash}
              </div>
            )}
            {testMode && (
              <div className="absolute top-3 right-3 px-2 py-1 bg-green-600/90 rounded text-white text-[9px] font-bold z-10 pointer-events-none" data-testid="test-mode-badge">TEST FILTRES ACTIF</div>
            )}
            {videoUrl && isPlayableVideoUrl(videoUrl) && !videoError ? (
              <video ref={videoRef} src={videoUrl} className="h-full max-w-full" controls={false}
                onClick={() => { const v = videoRef.current; if (v.paused) { safePlay(v); } else v.pause(); }}
                onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
                onLoadedMetadata={e => { setDuration(e.target.duration || 0); e.target.playbackRate = speed; }}
                onTimeUpdate={onTimeUpdate}
                onError={() => { setPlaying(false); setVideoError('Impossible de lire cette vidéo : fichier introuvable ou format non supporté par le navigateur.'); }}
                data-testid="timeline-video" />
            ) : videoUrl ? (
              <div className="flex flex-col items-center gap-2 px-8 text-center" data-testid="timeline-video-error">
                <AlertTriangle size={22} className="text-orange-400" />
                <p className="text-orange-300 text-xs font-semibold max-w-md">
                  {videoError || 'Le fichier vidéo principal de ce contenu est invalide (ce n\'est pas une vidéo).'}
                </p>
                <p className="text-white/40 text-[10px] max-w-md">Re-téléversez la vidéo principale (MP4, WebM, MKV ou MOV) depuis la fiche du contenu, puis rouvrez cet éditeur.</p>
              </div>
            ) : <p className="text-white/40 text-xs">Pas de vidéo</p>}
          </div>
          {/* Transport bar */}
          <div className="flex items-center gap-3 px-4 py-2 border-y border-white/10 text-white">
            <button onClick={() => { const v = videoRef.current; if (!v) return; v.paused ? v.play() : v.pause(); }}
              className="p-1.5 bg-white/10 rounded-full hover:bg-white/20" data-testid="timeline-playpause">
              {playing ? <Pause size={13} /> : <Play size={13} />}
            </button>
            <span className="text-[11px] font-semibold tabular-nums" title="HH:MM:SS:image (25 i/s)">{fmtF(currentTime)} / {fmt(duration)}</span>
            <div className="flex items-center gap-0.5 ml-2" data-testid="speed-controls">
              {[1, 2, 4, 8, 16].map(s => (
                <button key={s} onClick={() => setRate(s)}
                  title={s === 1 ? 'Vitesse normale' : `Avance rapide ×${s}`}
                  className={`px-1.5 py-0.5 rounded text-[9px] font-bold tabular-nums transition-colors ${speed === s ? 'bg-[#E9641A] text-white' : 'bg-white/5 text-white/50 hover:bg-white/15 hover:text-white'}`}
                  data-testid={`speed-x${s}`}>×{s}</button>
              ))}
            </div>
            <div className="flex-1" />
            <button onClick={addFilter} className="flex items-center gap-1 px-2.5 py-1.5 bg-[#E9641A]/15 text-[#E9641A] text-[10px] font-semibold rounded-lg hover:bg-[#E9641A]/25" data-testid="timeline-add-filter">
              <Plus size={11} /> Ajouter un filtre
            </button>
            <button onClick={() => setTool('arrow')} title="Outil flèche (A)"
              className={`p-1.5 rounded ${tool === 'arrow' ? 'bg-white/20 text-white' : 'text-white/50 hover:text-white'}`}
              data-testid="tool-arrow"><MousePointer2 size={14} /></button>
            <button onClick={() => setTool('zoom')} title="Outil zoom (Z) — clic : zoom · Alt+clic : dézoom"
              className={`p-1.5 rounded ${tool === 'zoom' ? 'bg-white/20 text-white' : 'text-white/50 hover:text-white'}`}
              data-testid="tool-zoom"><ZoomIn size={14} /></button>
            <div className="w-px h-4 bg-white/15" />
            <button onClick={() => zoomAt(2)} className="p-1.5 text-white/60 hover:text-white" data-testid="timeline-zoom-in"><ZoomIn size={14} /></button>
            <button onClick={() => zoomAt(0.5)} className="p-1.5 text-white/60 hover:text-white" data-testid="timeline-zoom-out"><ZoomOut size={14} /></button>
          </div>
          {/* Tracks */}
          <div ref={scrollRef} className="flex-1 overflow-auto" data-testid="timeline-tracks"
            style={{ cursor: tool === 'zoom' ? (altDown ? 'zoom-out' : 'zoom-in') : undefined }}>
            <div style={{ width: timelineW + 130 }} className="relative">
              {/* Ruler */}
              <div className="flex sticky top-0 bg-[#0b0b12] z-10 border-b border-white/10">
                <div className="w-[130px] flex-none" />
                <div ref={rulerRef} className="relative h-6" style={{ width: timelineW, cursor: tool === 'zoom' ? 'inherit' : 'ew-resize' }} onPointerDown={onScrubDown}>
                  {marks.map(t => (
                    <span key={t} className="absolute text-[8px] text-white/40 border-l border-white/15 pl-0.5" style={{ left: t * pxPerSec, top: 4, height: 16 }}>{fmt(t)}</span>
                  ))}
                </div>
              </div>
              {cats.map(cat => {
                const color = colorOf(cat.id);
                return (
                  <div key={cat.id} className="flex items-stretch border-b border-white/5" data-testid={`track-${cat.id}`}>
                    <div className="w-[130px] flex-none flex items-center gap-1.5 px-2 py-2 sticky left-0 z-30 bg-[#0b0b12] border-r border-white/10">
                      <span className="w-2.5 h-2.5 rounded-sm flex-none" style={{ background: color }} />
                      <span className="text-[10px] text-white/70 font-semibold leading-tight">{cat.name}</span>
                    </div>
                    <div className="relative h-9" style={{ width: timelineW, background: 'rgba(255,255,255,0.03)', cursor: tool === 'zoom' ? 'inherit' : 'pointer' }} onPointerDown={onScrubDown}>
                      {items.filter(i => i.filter_id === cat.id).map(item => {
                        const isSel = item.key === selected;
                        const low = item.confidence !== null && item.confidence < LOW_CONFIDENCE;
                        return (
                          <div key={item.key}
                            onPointerDown={e => onBlockDown(e, item, 'move')}
                            className="absolute top-1 bottom-1 rounded cursor-grab active:cursor-grabbing select-none"
                            style={{
                              left: item.start * pxPerSec, width: Math.max(6, (item.end - item.start) * pxPerSec),
                              background: color + (isSel ? 'ee' : '99'),
                              outline: isSel ? '2px solid #fff' : low ? '2px solid #fb923c' : 'none',
                              boxShadow: low ? '0 0 8px rgba(251,146,60,0.6)' : 'none',
                            }}
                            title={`${cat.name} ${fmt(item.start)} → ${fmt(item.end)}${item.confidence !== null ? ` · ${item.confidence}%` : ''}`}
                            data-testid={`filter-block-${item.key}`}>
                            <div onPointerDown={e => onBlockDown(e, item, 'l')} className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize bg-black/30 rounded-l" />
                            <div onPointerDown={e => onBlockDown(e, item, 'r')} className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize bg-black/30 rounded-r" />
                            {low && <AlertTriangle size={9} className="absolute top-0.5 left-2 text-orange-200" />}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              {/* Playhead (draggable) */}
              <div className="absolute top-0 bottom-0 w-px bg-[#E9641A] pointer-events-none z-20"
                style={{ left: 130 + currentTime * pxPerSec }} data-testid="timeline-playhead">
                <div onPointerDown={onPlayheadDown} className="w-3 h-3 -ml-[6px] rotate-45 bg-[#E9641A] pointer-events-auto cursor-ew-resize" data-testid="playhead-handle" />
              </div>
            </div>
          </div>
        </div>

        {/* Right: detail panel */}
        <div className="w-72 flex-none border-l border-white/10 p-4 overflow-y-auto text-white" data-testid="timeline-detail-panel">
          {/* Filter test mode (preview skips/mutes during playback) */}
          <button onClick={() => {
            setTestMode(m => {
              const nm = !m;
              const v = videoRef.current;
              if (!nm && v) v.muted = false;
              return nm;
            });
          }}
            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg mb-4 transition-colors ${testMode ? 'bg-green-600/20 border border-green-500/50' : 'bg-white/5 border border-white/10 hover:bg-white/10'}`}
            data-testid="test-mode-toggle">
            <span className="text-[11px] font-bold">Test des filtres</span>
            <span className={`relative w-9 h-5 rounded-full transition-colors ${testMode ? 'bg-green-500' : 'bg-white/20'}`}>
              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${testMode ? 'left-[18px]' : 'left-0.5'}`} />
            </span>
          </button>
          <p className="text-[9px] text-white/35 -mt-2 mb-4 leading-relaxed">Activé : à la lecture, les scènes sont sautées et le son coupé comme le verront les utilisateurs. Désactivé : lecture normale.</p>
          {sel ? (
            <>
              <h3 className="text-xs font-bold mb-3 flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: colorOf(sel.filter_id) }} />
                Détail du filtre
                {sel.source === 'ai' && <span className="text-[8px] font-bold text-purple-300 bg-purple-500/15 px-1.5 py-0.5 rounded">IA</span>}
              </h3>
              <label className="block text-[10px] text-white/50 mb-1">Type de filtre</label>
              <select value={sel.filter_id} onChange={e => upd(sel.key, { filter_id: e.target.value })}
                className="w-full bg-white/5 border border-white/15 rounded-lg px-2 py-1.5 text-xs mb-3" data-testid="detail-type-select">
                {cats.map(c => <option key={c.id} value={c.id} className="bg-[#14141f]">{c.name}</option>)}
              </select>
              {['start', 'end'].map(fld => (
                <div key={fld} className="mb-3">
                  <label className="block text-[10px] text-white/50 mb-1">{fld === 'start' ? 'Heure de début' : 'Heure de fin'}</label>
                  <div className="flex items-center gap-1">
                    <button onClick={() => upd(sel.key, { [fld]: Math.max(0, sel[fld] - 1) })} className="px-2 py-1.5 bg-white/5 rounded text-xs hover:bg-white/10">−1s</button>
                    <input value={fmt(sel[fld])} onChange={e => upd(sel.key, { [fld]: parseTs(e.target.value) })}
                      className="flex-1 bg-white/5 border border-white/15 rounded-lg px-2 py-1.5 text-xs text-center tabular-nums" data-testid={`detail-${fld}`} />
                    <button onClick={() => upd(sel.key, { [fld]: Math.min(duration || 1e9, sel[fld] + 1) })} className="px-2 py-1.5 bg-white/5 rounded text-xs hover:bg-white/10">+1s</button>
                  </div>
                </div>
              ))}
              <div className="mb-4">
                <label className="block text-[10px] text-white/50 mb-1">Niveau de confiance de l'IA</label>
                {sel.confidence !== null ? (
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-white/10 rounded-full">
                      <div className="h-full rounded-full" style={{ width: `${sel.confidence}%`, background: sel.confidence < LOW_CONFIDENCE ? '#fb923c' : '#22c55e' }} />
                    </div>
                    <span className={`text-xs font-bold ${sel.confidence < LOW_CONFIDENCE ? 'text-orange-400' : 'text-green-400'}`} data-testid="detail-confidence">{sel.confidence}%</span>
                  </div>
                ) : <span className="text-[10px] text-white/40">Filtre manuel</span>}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => { seekTo(sel.start); safePlay(videoRef.current); }} className="flex items-center justify-center gap-1 px-2 py-2 bg-white/8 rounded-lg text-[10px] font-semibold hover:bg-white/15" data-testid="detail-play-scene"><Play size={10} /> Lire la scène</button>
                <button onClick={splitSel} className="flex items-center justify-center gap-1 px-2 py-2 bg-white/8 rounded-lg text-[10px] font-semibold hover:bg-white/15" data-testid="detail-split"><Scissors size={10} /> Découper</button>
                <button onClick={mergeSel} className="flex items-center justify-center gap-1 px-2 py-2 bg-white/8 rounded-lg text-[10px] font-semibold hover:bg-white/15" data-testid="detail-merge"><Merge size={10} /> Fusionner suiv.</button>
                <button onClick={removeSel} className="flex items-center justify-center gap-1 px-2 py-2 bg-red-500/15 text-red-400 rounded-lg text-[10px] font-semibold hover:bg-red-500/25" data-testid="detail-delete"><Trash2 size={10} /> Supprimer</button>
              </div>
            </>
          ) : (
            <p className="text-[11px] text-white/40 leading-relaxed">Cliquez sur un bloc de la timeline pour voir et modifier le filtre : type, début, fin, confiance IA.<br /><br />Glissez un bloc horizontalement pour le déplacer dans le temps, verticalement pour changer son type de filtre (piste), tirez ses bords pour ajuster début/fin.</p>
          )}
          {lowConf.length > 0 && (
            <div className="mt-5 pt-4 border-t border-white/10">
              <h4 className="text-[10px] font-bold text-orange-400 mb-2 flex items-center gap-1"><AlertTriangle size={10} /> À vérifier en priorité</h4>
              {lowConf.map(i => (
                <button key={i.key} onClick={() => { setSelected(i.key); seekTo(i.start); }}
                  className="w-full text-left flex items-center gap-1.5 px-2 py-1.5 rounded hover:bg-white/5 text-[10px] text-white/70"
                  data-testid={`lowconf-${i.key}`}>
                  <span className="w-2 h-2 rounded-sm flex-none" style={{ background: colorOf(i.filter_id) }} />
                  {fmt(i.start)} → {fmt(i.end)} · <span className="text-orange-400 font-bold">{i.confidence}%</span>
                </button>
              ))}
            </div>
          )}
          {/* FCPX-style shortcuts */}
          <div className="mt-5 pt-4 border-t border-white/10 text-[9px] text-white/35 leading-relaxed" data-testid="shortcuts-help">
            <p className="font-bold text-white/50 mb-1">Raccourcis (style Final Cut Pro)</p>
            <p>Espace : lecture / pause</p>
            <p>⌘Z / Ctrl+Z : annuler · ⇧⌘Z : rétablir</p>
            <p>⌘B : découper au curseur · ⌫ : supprimer</p>
            <p>← → : image par image · ⇧← → : 1 s · + − : zoom</p>
            <p>Z : outil zoom (clic = zoom, Alt+clic = dézoom) · A : outil flèche</p>
            <p>Glisser la règle ou la tête de lecture : déplacer le curseur</p>
            <p>Glisser un bloc verticalement : changer de type</p>
          </div>
        </div>
      </div>
    </div>
  );
}
